import { useState, useCallback } from 'react';
import { auth } from '../firebase';
import AnalyticsService from '../services/AnalyticsService';
import { reducedModeEnabled, reducedModeYouTubeApiKey, reducedModeYouTubeEnabled } from '../utils/reducedMode';

interface YouTubeSearchResult {
  id: string;
  videoId: string;
  title: string;
  channelTitle: string;
  thumbnail: string;
  duration?: string;
  publishedAt: string;
}

interface UseYouTubeSearchReturn {
  searchResults: YouTubeSearchResult[];
  isLoading: boolean;
  error: string | null;
  searchYouTube: (query: string, partyId?: string) => Promise<void>;
  clearResults: () => void;
}

export const useYouTubeSearch = (): UseYouTubeSearchReturn => {
  const [searchResults, setSearchResults] = useState<YouTubeSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const formatIsoDuration = (value?: string): string | undefined => {
    if (!value) return undefined;
    const match = /^PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?$/.exec(value);
    if (!match) return value;
    const hours = Number(match[1] || 0);
    const minutes = Number(match[2] || 0);
    const seconds = Number(match[3] || 0);
    if (hours > 0) {
      return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
  };

  const finalizeResults = async (results: YouTubeSearchResult[]) => {
    const filteredResults = await AnalyticsService.filterSearchResultsByQuality(results);

    const blockedMap = (() => {
      try {
        return JSON.parse(localStorage.getItem('qroke:blockedVideoIds') || '{}') as Record<string, any>;
      } catch {
        return {} as Record<string, any>;
      }
    })();

    const finalResults = filteredResults.filter((result) => {
      const videoId = result.videoId || result.id;
      return !blockedMap[videoId];
    });

    const blockedCount = filteredResults.length - finalResults.length;
    if (blockedCount > 0) {
      setError(`${blockedCount} blocked video${blockedCount > 1 ? 's were' : ' was'} hidden from results due to playback/quality issues.`);
    }

    setSearchResults(finalResults);
  };

  const searchYouTubeDirect = async (query: string) => {
    const searchUrl = new URL('https://www.googleapis.com/youtube/v3/search');
    searchUrl.searchParams.set('part', 'snippet');
    searchUrl.searchParams.set('q', `${query} karaoke`);
    searchUrl.searchParams.set('maxResults', '10');
    searchUrl.searchParams.set('type', 'video');
    searchUrl.searchParams.set('videoEmbeddable', 'true');
    searchUrl.searchParams.set('videoCategoryId', '10');
    searchUrl.searchParams.set('key', reducedModeYouTubeApiKey);

    const searchResponse = await fetch(searchUrl.toString());
    if (!searchResponse.ok) {
      throw new Error(`YouTube search failed with status ${searchResponse.status}`);
    }

    const searchData = await searchResponse.json();
    const items = Array.isArray(searchData.items) ? searchData.items : [];
    const ids = items.map((item: any) => item?.id?.videoId).filter(Boolean);

    let detailsMap: Record<string, any> = {};
    if (ids.length > 0) {
      const detailsUrl = new URL('https://www.googleapis.com/youtube/v3/videos');
      detailsUrl.searchParams.set('part', 'status,contentDetails,snippet');
      detailsUrl.searchParams.set('id', ids.join(','));
      detailsUrl.searchParams.set('key', reducedModeYouTubeApiKey);

      const detailsResponse = await fetch(detailsUrl.toString());
      if (!detailsResponse.ok) {
        throw new Error(`YouTube details failed with status ${detailsResponse.status}`);
      }

      const detailsData = await detailsResponse.json();
      for (const video of detailsData.items || []) {
        if (video?.id) detailsMap[video.id] = video;
      }
    }

    const formattedResults: YouTubeSearchResult[] = items
      .map((item: any) => {
        const id = item?.id?.videoId;
        const detail = id ? detailsMap[id] : null;
        const title = String(item?.snippet?.title || '').trim();
        const channelTitle = String(item?.snippet?.channelTitle || 'Unknown Channel');
        const thumbnail = item?.snippet?.thumbnails?.medium?.url || item?.snippet?.thumbnails?.default?.url || '';
        const publishedAt = item?.snippet?.publishedAt || new Date().toISOString();
        const duration = formatIsoDuration(detail?.contentDetails?.duration);
        const status = detail?.status;
        const regionBlocked = detail?.contentDetails?.regionRestriction?.blocked || [];
        const embeddable = status?.embeddable === true;
        const isPublic = status?.privacyStatus === 'public';
        const notBlockedInUS = !(Array.isArray(regionBlocked) && regionBlocked.includes('US'));
        const looksKaraoke = /karaoke/i.test(title) || /karaoke/i.test(channelTitle);
        return {
          id,
          videoId: id,
          title,
          channelTitle,
          thumbnail,
          duration,
          publishedAt,
          embeddable,
          isPublic,
          notBlockedInUS,
          looksKaraoke,
        };
      })
      .filter((video: any) => video.id && video.embeddable && video.isPublic && video.notBlockedInUS && video.looksKaraoke)
      .map(({ embeddable, isPublic, notBlockedInUS, looksKaraoke, ...video }: any) => video);

    await finalizeResults(formattedResults);
  };

  const searchYouTube = useCallback(async (query: string, partyId?: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    if (reducedModeEnabled) {
      if (!reducedModeYouTubeEnabled) {
        setSearchResults([]);
        setError('YouTube search is not configured for this party right now. You can still use the local catalog when the host has one.');
        return;
      }
    }

    setIsLoading(true);
    setError(null);

    try {
      if (reducedModeYouTubeEnabled) {
        await searchYouTubeDirect(query);
        return;
      }

      // Use HTTP request like your old app instead of callable function
      const apiUrl = 'https://youtube-ct3c6uafdq-uc.a.run.app'; // YouTube function URL
      // Try to include Firebase ID token if available
      let authHeader: Record<string, string> = {};
      try {
        const user = auth.currentUser;
        if (user) {
          const token = await user.getIdToken();
          authHeader = { Authorization: `Bearer ${token}` };
        }
      } catch {}

      const url = new URL(apiUrl);
      url.searchParams.set('q', query);
      url.searchParams.set('maxResults', '10');
      if (partyId) url.searchParams.set('partyId', partyId);

      const response = await fetch(url.toString(), { headers: { ...authHeader } });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
  console.log('YouTube search response:', data);
      
      if (Array.isArray(data)) {
        const formattedResults: YouTubeSearchResult[] = data.map((video: any) => ({
          id: video.id,
          videoId: video.id,
          title: video.title,
          channelTitle: video.channelTitle || 'Unknown Channel',
          thumbnail: video.thumbnail,
          duration: video.duration,
          publishedAt: video.publishedAt || new Date().toISOString()
        }));

        await finalizeResults(formattedResults);
      } else {
        console.log('Unexpected response format:', data);
        setSearchResults([]);
      }
    } catch (err: any) {
      console.error('YouTube search error:', err);
      const message = String(err?.message || '');
      if (reducedModeYouTubeEnabled && (message.includes('401') || message.includes('403'))) {
        const origin = typeof window !== 'undefined' ? window.location.origin : 'this site';
        setError(`YouTube browser-key search was rejected for ${origin}. Check that the API key is allowed for this domain and restricted to YouTube Data API v3.`);
      } else if (message.includes('401') || message.includes('403')) {
        setError('YouTube connection required. Hosts: connect YouTube in Dashboard.');
      } else {
        setError(message || 'Failed to search YouTube');
      }
      setSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearResults = useCallback(() => {
    setSearchResults([]);
    setError(null);
  }, []);

  return {
    searchResults,
    isLoading,
    error,
    searchYouTube,
    clearResults
  };
};